import {
  Flex,
  Text,
  Modal,
  ModalContent,
  ModalOverlay,
  ModalBody,
  Menu,
  Button,
  MenuButton,
  MenuItem,
  MenuList,
  MenuDivider,
  MenuGroup,
  Heading,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { createContext, useMemo } from 'react';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { CompositionGroupView } from '../../compositions/shared/group/CompositionGroupView';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import {
  Branch,
  VariantBranchSupplier,
} from '@texas/api/endpoints/variantsApi';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useOptionsOverview } from '../../options/standard/useOptionsOverview';
import { useVersionOverviewContext } from '../shared/VersionOverviewContext';

interface Props {
  variantId: number;
  suppliers: VariantBranchSupplier[];
  branches: Branch[];
}

export const OptionsContext = createContext<ReturnType<
  typeof useOptionsOverview
> | null>(null);

export function OptionsOverview({ variantId, suppliers, branches }: Props) {
  const options = useOptionsOverview({
    variantId,
  });
  const { filters } = useVersionOverviewContext();
  const { t } = useTranslation();
  const { value, isOpen, onClose, onOpen } = useValueDisclosure<number>();

  const filteredVersions = useMemo(() => {
    if (!options.versions) return [];
    if (!filters.optionIds?.length) return options.versions;
    return options.versions.filter((x) => filters.optionIds!.includes(x.id));
  }, [options.versions, filters.optionIds]);

  useSignalSubscriptionEvents({
    triggerOnEvent: onClose,
    signalEvents: [
      compositionGroupEvents.onArchived,
      compositionGroupEvents.onRestored,
    ],
  });

  return (
    <OptionsContext.Provider value={options}>
      <Heading fontSize="md" pb={2}>
        {t('general.options')}
      </Heading>
      <Flex gap={2} wrap="wrap">
        {options.versionsLoading && (
          <SpinnerWithLabel label={t('compositionGroup.option.loading')} />
        )}
        {options.versions?.length === 0 && (
          <Text variant="sub">{t('compositionGroup.option.noOptions')}</Text>
        )}
        {filteredVersions.map((x) => (
          <Menu key={x.id} isLazy={true}>
            <MenuButton
              as={Button}
              rightIcon={<Icons.ChevronDown />}
              h="auto"
              textAlign="start"
              py={2}
              isLoading={options.isCreating}
              isDisabled={!x.isReady}
              variant="texas-light"
              maxW={72}
              whiteSpace="normal"
            >
              <Flex flexDir="column" gap={2}>
                <CompositionGroupTitle
                  version={x.version}
                  note={x.note}
                  type="option"
                  fontSize="md"
                  linkedVariantId={x.linkedVariantId}
                />
                {!x.isReady && (
                  <Text
                    fontSize="sm"
                    fontWeight="normal"
                    color="yellow.400"
                    _light={{ color: 'orange.500' }}
                  >
                    {t('compositionGroup.option.notYetReady')}
                  </Text>
                )}
              </Flex>
            </MenuButton>

            <MenuList>
              <MenuItem
                icon={<Icons.OpenInNew boxSize={defaultIconSize} />}
                onClick={() => onOpen(x.id)}
              >
                {t('compositionGroup.option.preview')}
              </MenuItem>
              <MenuDivider />
              {branches.map((branch) => {
                const branchSuppliers = suppliers.filter(
                  (s) =>
                    s.branchId === branch.id &&
                    !s.disabled &&
                    (!filters.supplierIds?.length ||
                      filters.supplierIds.includes(s.supplierId)) &&
                    (!filters.branchIds?.length ||
                      filters.branchIds.includes(s.branchId)),
                );
                if (branchSuppliers.length === 0) return null;
                return (
                  <MenuGroup key={branch.id} title={branch.identifier}>
                    {branchSuppliers.map((s) => (
                      <MenuItem
                        key={`${s.supplierId}-${s.branchId}`}
                        icon={<Icons.Plus boxSize={defaultIconSize} />}
                        onClick={() =>
                          options.copyGroup({
                            fromId: x.id,
                            location: {
                              variantId: x.variantId,
                              supplierId: s.supplierId,
                              branchId: s.branchId,
                            },
                            basedOnId: x.id,
                          })
                        }
                      >
                        {t('compositionGroup.option.addToSupplier', {
                          name: s.supplierName,
                        })}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                );
              })}
            </MenuList>
          </Menu>
        ))}
      </Flex>
      <Modal isCentered={true} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent width="auto" maxW="fit-content">
          <ModalCloseButton />
          <ModalBody>{value && <InnerModal id={value} />}</ModalBody>
        </ModalContent>
      </Modal>
    </OptionsContext.Provider>
  );
}

function InnerModal({ id }: { id: number }) {
  return (
    <>
      <CompositionGroupView
        variantId={0}
        compositionGroupId={id}
        componentSettings={{
          expandedDefault: true,
          addMaterials: 'hidden',
          materialsMode: 'read-only',
          removeMaterials: 'hidden',
          editImage: 'hidden',
          dimensionMode: 'read-only',
          compositionArchiveRestore: 'hidden',
        }}
      />
    </>
  );
}
